import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, hideClose, mobileCard = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={cn("md:bg-black/80 bg-black/95", mobileCard && "!bg-slate-950/70 backdrop-blur-sm")} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // Mobile: full-screen flat page (slide up from bottom)
        "fixed z-50 grid w-full border bg-background shadow-lg duration-200",
        "left-0 right-0 top-[var(--app-viewport-top,0px)] h-[var(--app-viewport-height,100dvh)] max-h-[var(--app-viewport-height,100dvh)] overflow-y-auto overscroll-contain rounded-none px-4 pb-[calc(1rem+var(--app-safe-bottom))] pt-[calc(3.75rem+var(--app-safe-top))]",
        "data-[state=open]:animate-in data-[state=closed]:animate-out",
        "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
        // Desktop: centered modal (existing behavior)
        "md:inset-auto md:left-[50%] md:top-[50%] md:h-auto md:translate-x-[-50%] md:translate-y-[-50%]",
        "md:max-w-lg md:max-h-[85vh] md:rounded-lg md:p-6 md:pt-6",
        "md:data-[state=open]:slide-in-from-left-1/2 md:data-[state=open]:slide-in-from-top-[48%]",
        "md:data-[state=closed]:slide-out-to-left-1/2 md:data-[state=closed]:slide-out-to-top-[48%]",
        "md:data-[state=open]:zoom-in-95 md:data-[state=closed]:zoom-out-95",
        "md:data-[state=open]:fade-in-0 md:data-[state=closed]:fade-out-0",
        className,
        mobileCard && [
          "!inset-auto !left-1/2 !top-1/2 !h-auto !w-[calc(100%_-_2rem)] !max-w-md !max-h-[min(82dvh,44rem)] !-translate-x-1/2 !-translate-y-1/2",
          "!overflow-y-auto !overscroll-contain !rounded-3xl !border-border !bg-card !text-card-foreground !p-4 sm:!p-5",
          "gap-3 shadow-[0_24px_80px_rgba(2,6,23,0.38)] ring-1 ring-white/5",
          "data-[state=open]:!slide-in-from-bottom-0 data-[state=closed]:!slide-out-to-bottom-0",
          "data-[state=open]:!zoom-in-95 data-[state=closed]:!zoom-out-95",
        ]
      )}
      data-scoring-dialog={mobileCard ? "true" : undefined}
      {...props}>
      {children}
      {!hideClose && (
        <DialogPrimitive.Close
          data-dialog-close="true"
          className="absolute right-3 top-[calc(0.5rem+var(--app-safe-top))] z-10 flex h-11 w-11 items-center justify-center rounded-md opacity-80 ring-offset-background transition-[background-color,opacity] hover:bg-accent hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground md:right-4 md:top-4 md:h-9 md:w-9">
          <X className="h-5 w-5 md:h-4 md:w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-tight tracking-normal", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
